/**
 * Routes a Scene to its specific renderer component.
 * Adding a new scene type = add a case here + the component + manifest/types/zod entry.
 */

import type { Scene } from '@/types/gameSpec';
import type { SceneComponentProps } from './sceneCommon';

import { MCQSceneView } from './MCQSceneView';
import { FillBlankSceneView } from './FillBlankSceneView';
import { FlashcardSceneView } from './FlashcardSceneView';
import { TapCorrectSceneView } from './TapCorrectSceneView';
import { SequenceInputSceneView } from './SequenceInputSceneView';
import { MatchingSceneView } from './MatchingSceneView';
import { OrderingSceneView } from './OrderingSceneView';
import { DragToBucketSceneView } from './DragToBucketSceneView';
import { MemoryPairsSceneView } from './MemoryPairsSceneView';
import { NumberLineSceneView } from './NumberLineSceneView';
import { WorkedStepsSceneView } from './WorkedStepsSceneView';
import { ReadingSceneView } from './ReadingSceneView';
import { SprintSceneView } from './SprintSceneView';

export const SceneRouter = ({
  scene,
  onAnswer,
}: SceneComponentProps<Scene>) => {
  switch (scene.type) {
    case 'mcq':            return <MCQSceneView scene={scene} onAnswer={onAnswer} />;
    case 'fill_blank':     return <FillBlankSceneView scene={scene} onAnswer={onAnswer} />;
    case 'flashcard':      return <FlashcardSceneView scene={scene} onAnswer={onAnswer} />;
    case 'tap_correct':    return <TapCorrectSceneView scene={scene} onAnswer={onAnswer} />;
    case 'sequence_input': return <SequenceInputSceneView scene={scene} onAnswer={onAnswer} />;
    case 'matching':       return <MatchingSceneView scene={scene} onAnswer={onAnswer} />;
    case 'ordering':       return <OrderingSceneView scene={scene} onAnswer={onAnswer} />;
    case 'drag_to_bucket': return <DragToBucketSceneView scene={scene} onAnswer={onAnswer} />;
    case 'memory_pairs':   return <MemoryPairsSceneView scene={scene} onAnswer={onAnswer} />;
    case 'number_line':    return <NumberLineSceneView scene={scene} onAnswer={onAnswer} />;
    case 'worked_steps':   return <WorkedStepsSceneView scene={scene} onAnswer={onAnswer} />;
    case 'reading':        return <ReadingSceneView scene={scene} onAnswer={onAnswer} />;
    case 'sprint':         return <SprintSceneView scene={scene} onAnswer={onAnswer} />;
    default: {
      // Exhaustive check — TS will error here if a new type is added without a case
      const _exhaustive: never = scene;
      return null;
    }
  }
};
